// src/lib/sample-data/index.ts - Sample data exports
// 
// SECURITY NOTE: This module should NEVER be imported or used in production.
// Production environments must use NEXT_PUBLIC_USE_SAMPLE_DATA=false.

// Security check: Prevent sample data usage in production deployments only
// Staging is allowed to use sample data for demos and testing
const isProductionDeployment = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' || 
                               process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID === 'courtly-production';

// Check if Firebase credentials are placeholder values (indicating incomplete setup)
const hasPlaceholderCredentials = process.env.FIREBASE_PRIVATE_KEY?.includes('PLACEHOLDER') ||
                                  process.env.FIREBASE_PRIVATE_KEY?.includes('YOUR_') ||
                                  !process.env.FIREBASE_PRIVATE_KEY;

// Allow sample data in production builds when credentials are incomplete/placeholder
if (typeof window !== 'undefined' && 
    isProductionDeployment && 
    process.env.NEXT_PUBLIC_USE_SAMPLE_DATA === 'true' && 
    !hasPlaceholderCredentials) {
  console.error('SECURITY ERROR: Sample data is enabled in production environment.');
  throw new Error('Sample data cannot be used in production. Set NEXT_PUBLIC_USE_SAMPLE_DATA=false.');
}

import { SAMPLE_DATA_CONFIG, DEFAULT_CLUB } from './config';
import { createSampleOrganization, createSampleClub, createSampleSpace } from './utils';
import type { Space } from '@/lib/types';
import { sampleClubsData } from './clubs';
import { sampleAccountSettingsStore } from './settings';
import { sampleAdminSettings } from './settings';
import { sampleInvoicesData } from './invoices';
import { sampleRecentPayments, samplePaymentsData } from './payments';
import { samplePaymentMethodsData } from './payment-methods';
import { sampleSuperAdminInsights } from './super-admin-insights';
import { 
  SAMPLE_USER_ORGANIZATION_MAPPINGS, 
  getUserOrganizationMapping, 
  getUserOrganizationMappingByEmail, 
  getUsersForOrganization, 
  isSampleDataUser, 
  getSampleDataTenantId 
} from './userOrganizationMapping';

// Create base sample data
export const sampleOrganization = createSampleOrganization();

export const sampleClub = createSampleClub({
  organizationId: sampleOrganization.id,
});

// Create sample spaces
export const sampleSpaces: Space[] = [
  createSampleSpace({
    displayName: 'Center Court',
    courtType: 'Tennis',
    spaceType: 'Court',
  }),
  createSampleSpace({
    displayName: 'Court 1',
    courtType: 'Tennis',
    spaceType: 'Court',
  }),
  // Add more sample spaces as needed
];

// Export everything
export {
  SAMPLE_DATA_CONFIG,
  DEFAULT_CLUB,
  sampleClubsData,
  sampleAccountSettingsStore,
  sampleAdminSettings,
  sampleInvoicesData,
  sampleRecentPayments,
  samplePaymentsData,
  samplePaymentMethodsData,
  sampleSuperAdminInsights,
  // User organization mappings
  SAMPLE_USER_ORGANIZATION_MAPPINGS,
  getUserOrganizationMapping,
  getUserOrganizationMappingByEmail,
  getUsersForOrganization,
  isSampleDataUser,
  getSampleDataTenantId
};

// Re-export everything from modules
export * from './config';
export * from './clubs';
export * from './spaces';
export * from './subscriptions';
export * from './organizations';
export * from './platform-plans';
export * from './platform-configs';
export * from './bookings';
export * from './settings';

export * from './invoices';
export * from './payments';
export * from './payment-methods';
export * from './plan-history';
export * from './super-admin-insights';
export * from './userOrganizationMapping';
export * from './users';
